import { createEffect, createSignal, type Accessor } from 'solid-js'
import type { CurrentUser } from '~/services/bootstrap'

export interface ShopBalances {
  coin: number
  gem: number
}

export interface SessionLedger {
  balances: Accessor<ShopBalances | undefined>
  credit: (currency: keyof ShopBalances, amount: number) => void
  debit: (currency: keyof ShopBalances, amount: number) => void
}

export function canAfford(
  balances: ShopBalances | undefined,
  cost: { currency: keyof ShopBalances; price: number },
): boolean {
  return (balances?.[cost.currency] ?? 0) >= cost.price
}

export function createSessionLedger(
  currentUser: Accessor<CurrentUser | undefined>,
): SessionLedger {
  const [balances, setBalances] = createSignal<ShopBalances>()
  let seeded = false

  createEffect(() => {
    const user = currentUser()
    if (seeded || !user) return
    seeded = true
    setBalances({ coin: user.coin, gem: user.gem })
  })

  const adjust = (currency: keyof ShopBalances, delta: number) => {
    setBalances((previous) =>
      previous
        ? {
            ...previous,
            [currency]: Math.max(0, previous[currency] + delta),
          }
        : previous,
    )
  }

  return {
    balances,
    credit: (currency, amount) => adjust(currency, amount),
    debit: (currency, amount) => adjust(currency, -amount),
  }
}
